import { Dispatch } from 'react';
import { getDayStart, getDayEnd } from 'src/app/common/utils';
import { ADMIN_URL, apiClient, apiErrorHandler } from 'src/app/common/network';
import { forEach, isArray } from 'lodash';
import { PaginateList } from 'src/app/common/types/common-types';
import {
  CampaignItem,
  CampaignDropdownType,
  CampaignDropdownList,
  MaximumLeadsItem,
  CampaignLeadItem,
  LeadWarningMessageItem,
  DropLeadRuleInterface,
  CampaignCategoryItem,
  TalkbotRuleInterface,
  CampaignSelectedSalesMaterialItem,
  SalesMaterialTypeEnum,
} from '../types/campaign-types';
import { getConfig } from 'src/app/i18n';

const campaignEndpoint = 'campaigns';
const leadEndpoint = 'leads';

export type CampaignListParam = {
  name: string;
  startDateFrom: Date | null;
  startDateTo: Date | null;
  endDateFrom: Date | null;
  endDateTo: Date | null;
  sort: string;
  status: string;
  page: number;
  limit: number;
  types?: string[];
  campaignCategory?: string; //PHKL Custom
};

export type CampaignDropdownListParam = {
  type: CampaignDropdownType[];
  parent?: string;
};

export type CampaignLeadListParam = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  agentCode: string;
  leadDateFrom: Date | null;
  leadDateTo: Date | null;
  status: string;
  isActive: string;
  page: number;
  limit: number;
};

export type ConfigParam = {
  name: string;
  namespace: string;
};

export type AttachmentBody = {
  blobId: string;
  filename: string;
};

export type CampaignFaqBody = {
  question: string;
  answer: string;
};

export type CampaignLinkBody = {
  title: string;
  link: string;
};

export type CampaignPriorityBody = {
  step: string;
};

export type CampaignFlyerBody = {
  title: string;
  attachments?: AttachmentBody[];
};

export type CampaignMessageBody = {
  title: string;
  body: string;
};

export type CreateCampaignBody = {
  campaignId: string;
  name: string;
  campaignType?: string;
  tags?: string[];
  description: string;
  regionalCampaignName?: string;
  type: string;
  sort?: string;
  startDate: Date | null;
  endDate: Date | null;
  launchDate?: Date;
  emailMessage?: CampaignMessageBody;
  notificationMessage?: CampaignMessageBody;
  mandatoryData: Array<string>;
  isNewLeadNotificationEnabled: boolean;
  callScripts?: string;
  saleTipsInsight?: string;
  faq: CampaignFaqBody[];
  links: CampaignLinkBody[];
  priorities: CampaignPriorityBody[];
  flyers: CampaignFlyerBody[];
  image?: AttachmentBody;
  maxLeadPerAgent?: number;
  marketingOwnerName: string;
  marketingOwnerEmail: string;
  status: string;
  source: string;
  subSource?: string[];
  acceptSLA?: string;
  contactSLA?: string;
  meetSLA?: string;
  proposalSLA?: string;
  applicationSLA?: string;
  customerIncentive?: string;
  agentIncentive?: string;
  isSelfCreateLeadEnabled: boolean;
  maximumMarketingLeads?: MaximumLeadsItem;
  maximumLeadSourceLeads?: MaximumLeadsItem;
  isAutoAssignLeadIfAgentInactive?: boolean;
  isLeadWithPartialInfo: boolean;
  leadWithPartialInfoWarning?: LeadWarningMessageItem;
  leadWithPartialCondition?: any;
  distributionRule?: string;
  eligibilityRule?: string;
  leadDedupRule?: string;
  referralAgentCheck?: string;
  customerDedupRule?: string[];
  customerDedup?: string;
  exclusionRule?: string;
  isEngagementRequired: boolean;
  isStaging: boolean;
  leadAction: string;
  prospectCategory?: string;
  distributionChannel?: string;
  targetProductType?: string[];
  feedbacks?: string[];
  feedbackGroup?: string;
  roundRobinMode: string;
  autoDropRule?: DropLeadRuleInterface;
  createdBy: string;
  updatedBy: string;
  campaignCategory?: string; //PHKL Custom
  talkbotRule?: TalkbotRuleInterface;
  selectedSalesMaterials?: CampaignSelectedSalesMaterialItem[];
};

export type UpdateCampaignBody = {
  campaignId: string;
  name: string;
  campaignType?: string;
  tags?: string[];
  description: string;
  regionalCampaignName?: string;
  type: string;
  sort?: string;
  startDate: Date | null;
  endDate: Date | null;
  launchDate?: Date;
  emailMessage?: CampaignMessageBody;
  notificationMessage?: CampaignMessageBody;
  mandatoryData: Array<string>;
  isNewLeadNotificationEnabled: boolean;
  callScripts?: string;
  saleTipsInsight?: string;
  faq: CampaignFaqBody[];
  links: CampaignLinkBody[];
  priorities: CampaignPriorityBody[];
  flyers: CampaignFlyerBody[];
  image?: AttachmentBody;
  maxLeadPerAgent?: number;
  marketingOwnerName: string;
  marketingOwnerEmail: string;
  status: string;
  source: string;
  subSource?: string[];
  acceptSLA?: string;
  contactSLA?: string;
  meetSLA?: string;
  proposalSLA?: string;
  applicationSLA?: string;
  customerIncentive?: string;
  agentIncentive?: string;
  isSelfCreateLeadEnabled: boolean;
  maximumMarketingLeads?: MaximumLeadsItem;
  maximumLeadSourceLeads?: MaximumLeadsItem;
  isAutoAssignLeadIfAgentInactive?: boolean;
  isLeadWithPartialInfo: boolean;
  leadWithPartialInfoWarning?: LeadWarningMessageItem;
  leadWithPartialCondition?: any;
  distributionRule?: string;
  eligibilityRule?: string;
  leadDedupRule?: string;
  referralAgentCheck?: string;
  customerDedupRule?: string[];
  customerDedup?: string;
  exclusionRule?: string;
  isEngagementRequired: boolean;
  isStaging: boolean;
  leadAction: string;
  prospectCategory?: string;
  distributionChannel?: string;
  targetProductType?: string[];
  feedbacks?: string[];
  feedbackGroup?: string;
  roundRobinMode: string;
  autoDropRule?: DropLeadRuleInterface;
  updatedBy: string;
  campaignCategory?: string; //PHKL Custom
  talkbotRule?: TalkbotRuleInterface;
  selectedSalesMaterials?: CampaignSelectedSalesMaterialItem[];
};

export type UpdateCampaignStatusBody = {
  status: string;
  updatedBy: string;
};

export type CampaignSaleMaterialListParam = {
  page: number;
  limit: number;
  name: string;
  section: string;
  category: string;
  status: string;
  type: string;
  createTimeStart: Date | null;
  createTimeEnd: Date | null;
  lastUpdatedTimeStart: Date | null;
  lastUpdatedTimeEnd: Date | null;
  effectiveDateStartFrom: Date | null;
  effectiveDateStartTo: Date | null;
  effectiveDateEndFrom: Date | null;
  effectiveDateEndTo: Date | null;
  locale: string;
};

export type CampaignSaleMaterialItem = {
  uuid: string;
  name: string;
  section: string;
  sectionKey: string;
  category: string;
  status: string;
  type: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  lastUpdatedBy?: string;
  effectiveDate?: Date | string;
  expiredDate?: Date | string;
  publishedAt?: Date | string;
  createdBy?: string;
};

export type CampaignSaleMaterialListResponse = {
  items: CampaignSaleMaterialItem[];
  page: number;
  totalPages: number;
  totalRecords: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
};

type CampaignSaleMaterialPagination = {
  page: number;
  pageCount: number;
  total: number;
};

type CampaignSaleMaterialRawItem = {
  uuid: string;
  name?: string;
  module?: string;
  category?: { name?: string } | null;
  status?: string;
  type?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  lastUpdatedBy?: string;
  effectiveDate?: Date | string;
  expiredDate?: Date | string;
  publishedAt?: Date | string;
};

type CampaignSaleMaterialRawResponse = {
  results: CampaignSaleMaterialRawItem[];
  pagination: CampaignSaleMaterialPagination;
};

type CampaignCustomerSurveyRawItem = {
  id?: string | number;
  _id?: string | number;
  title?: string;
  body?: {
    title?: string;
  };
  status?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  createdBy?: string;
};

type CampaignCustomerSurveyRawResponse = {
  items: CampaignCustomerSurveyRawItem[];
  totalPages: number;
  totalRecords: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
};

/**
 * build query path for getting campaign list
 * @param queryPath
 * @param params
 * @returns
 */
const buildQueryPath = (queryPath: string, params: CampaignListParam) => {
  forEach(params, (param, key) => {
    if (typeof param === 'object') {
      if (param) {
        if (key.toLowerCase().includes('from')) {
          queryPath += `${key}=${encodeURIComponent(getDayStart(param as Date).toISOString())}&`;
        }
        if (key.toLowerCase().includes('to')) {
          queryPath += `${key}=${encodeURIComponent(getDayEnd(param as Date).toISOString())}&`;
        }
      }
      if (isArray(param) && key === 'types') {
        param.forEach((value) => {
          queryPath += `${key}=${value}&`;
        });
      }
    } else {
      if (key === 'page') {
        queryPath += `${key}=${encodeURIComponent(param as any)}&`;
      } else {
        queryPath += param ? `${key}=${encodeURIComponent(param as any)}&` : '';
      }
    }
  });
  return queryPath;
};

const CAMPAIGN_MATERIAL_SECTION_LABEL_MAP: Record<string, string> = {
  Sales: 'Sales Resources',
  Promotion: 'Promotion Library',
  HappyShare: 'Happy Share Library',
  CustomerSurvey: 'Customer Survey',
};

const escapeCampaignSaleMaterialParam = (value: string) =>
  value.replace(/([!@#$%^&*()+=\[\]\\';,./{}|":<>?~_-])/g, '\\$1');

const isCustomerSurveySection = (section: string) => section === 'CustomerSurvey';

const getUtcDayStart = (date: Date) => getDayStart(date).toISOString();

const getUtcDayEnd = (date: Date) => getDayEnd(date).toISOString();

const CUSTOMER_SURVEY_DATE_PARAM_KEY_MAP: Record<string, string> = {
  createTimeStart: 'createdAtFrom',
  createTimeEnd: 'createdAtTo',
  lastUpdatedTimeStart: 'updatedAtFrom',
  lastUpdatedTimeEnd: 'updatedAtTo',
  effectiveDateStartFrom: 'startDateFrom',
  effectiveDateStartTo: 'startDateTo',
  effectiveDateEndFrom: 'endDateFrom',
  effectiveDateEndTo: 'endDateTo',
};

const buildCustomerSurveyQueryPath = (params: CampaignSaleMaterialListParam) => {
  const searchParams = new URLSearchParams();
  let hasSortParam = false;

  forEach(params, (param, key) => {
    if (['category', 'type', 'section', 'locale'].includes(key)) {
      return;
    }

    if (typeof param === 'object') {
      if (param) {
        const mappedKey = CUSTOMER_SURVEY_DATE_PARAM_KEY_MAP[key] || key;
        const normalizedKey = mappedKey.toLowerCase();

        if (mappedKey.includes('Sort')) {
          hasSortParam = true;
        }

        if (normalizedKey.includes('to') || normalizedKey.endsWith('end')) {
          searchParams.append(mappedKey, getUtcDayEnd(param as Date));
          return;
        }

        if (normalizedKey.includes('from') || normalizedKey.endsWith('start')) {
          searchParams.append(mappedKey, getUtcDayStart(param as Date));
        }
      }

      return;
    }

    if (key === 'page' || key === 'limit') {
      searchParams.append(key, String(param));
      return;
    }

    if (param) {
      if (key.includes('Sort')) {
        hasSortParam = true;
      }

      searchParams.append(key, String(param));
    }
  });

  if (!hasSortParam) {
    searchParams.append('lastUpdateDateSort', 'descending');
  }

  return `${ADMIN_URL}/customer-surveys?${searchParams.toString()}`;
};

const buildCampaignSaleMaterialQueryPath = (params: CampaignSaleMaterialListParam) => {
  const searchParams = new URLSearchParams();

  if (params.section) {
    searchParams.append('filters[module][$eq]', params.section);
    searchParams.append('filters[module][$containsi]', params.section);
  }

  searchParams.append('page', String(params.page));
  searchParams.append('pageSize', String(params.limit));

  if (params.name) {
    searchParams.append('filters[name][$containsi]', escapeCampaignSaleMaterialParam(params.name));
  }

  if (params.category) {
    searchParams.append('filters[category][name][$eq]', params.category);
  }

  if (params.status) {
    searchParams.append('filters[status][$eq]', params.status);
  }

  if (params.type) {
    searchParams.append('filters[type][$containsi]', params.type);
  }

  if (params.createTimeStart) {
    searchParams.append('filters[createdAt][$gte]', getUtcDayStart(params.createTimeStart));
  }

  if (params.createTimeEnd) {
    searchParams.append('filters[createdAt][$lte]', getUtcDayEnd(params.createTimeEnd));
  }

  if (params.lastUpdatedTimeStart) {
    searchParams.append('filters[updatedAt][$gte]', getUtcDayStart(params.lastUpdatedTimeStart));
  }

  if (params.lastUpdatedTimeEnd) {
    searchParams.append('filters[updatedAt][$lte]', getUtcDayEnd(params.lastUpdatedTimeEnd));
  }

  if (params.effectiveDateStartFrom) {
    searchParams.append('filters[effectiveDate][$gte]', getUtcDayStart(params.effectiveDateStartFrom));
  }

  if (params.effectiveDateStartTo) {
    searchParams.append('filters[effectiveDate][$lte]', getUtcDayEnd(params.effectiveDateStartTo));
  }

  if (params.effectiveDateEndFrom) {
    searchParams.append('filters[expiredDate][$gte]', getUtcDayStart(params.effectiveDateEndFrom));
  }

  if (params.effectiveDateEndTo) {
    searchParams.append('filters[expiredDate][$lte]', getUtcDayEnd(params.effectiveDateEndTo));
  }

  searchParams.append('sort', 'updatedAt:DESC');

  if (params.locale) {
    searchParams.append('locale', params.locale);
  }

  return `${ADMIN_URL}/content-manager/collection-types/api::material.material?${searchParams.toString()}`;
};

const mapCampaignSaleMaterialItem = (item: CampaignSaleMaterialRawItem): CampaignSaleMaterialItem => {
  const sectionKey = item.module as string;

  return {
    uuid: item.uuid,
    name: item.name ?? 'N/A',
    section: CAMPAIGN_MATERIAL_SECTION_LABEL_MAP[sectionKey] || sectionKey,
    sectionKey,
    category: item.category?.name ?? 'N/A',
    status: item.status ?? 'N/A',
    type: item.type ?? 'N/A',
    createdAt: item.createdAt ?? 'N/A',
    updatedAt: item.updatedAt ?? 'N/A',
    lastUpdatedBy: item.lastUpdatedBy ?? 'N/A',
    effectiveDate: item.effectiveDate ?? 'N/A',
    expiredDate: item.expiredDate ?? 'N/A',
    publishedAt: item.publishedAt ?? 'N/A',
  };
};

const mapCampaignCustomerSurveyItem = (item: CampaignCustomerSurveyRawItem): CampaignSaleMaterialItem => ({
  uuid: String(item.id ?? item._id ?? ''),
  name: item.title ?? item?.body?.title ?? 'N/A',
  section: CAMPAIGN_MATERIAL_SECTION_LABEL_MAP.CustomerSurvey,
  sectionKey: 'CustomerSurvey',
  category: 'N/A',
  type: 'N/A',
  createdAt: item.createdAt ?? 'N/A',
  updatedAt: item.updatedAt ?? 'N/A',
  lastUpdatedBy: item.createdBy ?? 'N/A',
  effectiveDate: item.startDate ?? 'N/A',
  expiredDate: item.endDate ?? 'N/A',
  publishedAt: item.updatedAt ?? 'N/A',
  status: item.status ?? 'N/A',
});

const isSurveySelectedSalesMaterial = (material: Pick<CampaignSelectedSalesMaterialItem, 'type' | 'section'>) =>
  material.type === SalesMaterialTypeEnum.survey ||
  material.section === CAMPAIGN_MATERIAL_SECTION_LABEL_MAP.CustomerSurvey;

const normalizeSelectedSalesMaterials = (materials?: CampaignSelectedSalesMaterialItem[]) =>
  materials
    ?.map((material, index) => {
      const uuid = material.uuid;

      if (!uuid) {
        return null;
      }

      return {
        uuid,
        type: isSurveySelectedSalesMaterial(material) ? SalesMaterialTypeEnum.survey : SalesMaterialTypeEnum.material,
        order: index,
      };
    })
    .filter((material): material is NonNullable<typeof material> => Boolean(material));

const buildCampaignSaleMaterialByUuidQueryPath = (uuids: string[], locale?: string) => {
  const searchParams = new URLSearchParams();

  uuids.forEach((uuid, index) => {
    searchParams.append(`filters[uuid][$in][${index}]`, uuid);
  });

  searchParams.append('page', '1');
  searchParams.append('pageSize', String(Math.max(uuids.length, 1)));
  searchParams.append('sort', 'updatedAt:DESC');

  if (locale) {
    searchParams.append('locale', locale);
  }

  return `${ADMIN_URL}/content-manager/collection-types/api::material.material?${searchParams.toString()}`;
};

const buildCustomerSurveyByIdsQueryPath = (ids: string[]) => {
  let queryPath = `${ADMIN_URL}/customer-surveys?page=1&limit=${encodeURIComponent(Math.max(ids.length, 1))}`;

  ids.forEach((id) => {
    queryPath += `&ids=${encodeURIComponent(id)}`;
  });

  return queryPath;
};

const buildSelectedSalesMaterialFallbackItem = (
  material: CampaignSelectedSalesMaterialItem,
): CampaignSelectedSalesMaterialItem => ({
  uuid: material.uuid,
  name: '',
  section: material.type === SalesMaterialTypeEnum.survey ? CAMPAIGN_MATERIAL_SECTION_LABEL_MAP.CustomerSurvey : '',
  type: material.type,
  status: '',
  order: material.order,
});

const hydrateSelectedSalesMaterials = async (
  materials?: CampaignSelectedSalesMaterialItem[],
  dispatch?: Dispatch<any>,
): Promise<CampaignSelectedSalesMaterialItem[] | undefined> => {
  if (!materials?.length) {
    return materials;
  }

  const sortedMaterials = [...materials].sort((left, right) => left.order - right.order);
  const materialUuids = sortedMaterials
    .filter((item) => item.type === SalesMaterialTypeEnum.material)
    .map((item) => item.uuid)
    .filter(Boolean);
  const surveyIds = sortedMaterials
    .filter((item) => item.type === SalesMaterialTypeEnum.survey)
    .map((item) => item.uuid)
    .filter(Boolean);
  const locale = getConfig().selectedLang;

  const [materialResponse, surveyResponse] = await Promise.all([
    materialUuids.length > 0
      ? apiClient.get<CampaignSaleMaterialRawResponse>(buildCampaignSaleMaterialByUuidQueryPath(materialUuids, locale))
      : Promise.resolve(null),
    surveyIds.length > 0
      ? apiClient.get<CampaignCustomerSurveyRawResponse>(buildCustomerSurveyByIdsQueryPath(surveyIds))
      : Promise.resolve(null),
  ]).catch((err) => {
    throw apiErrorHandler(err, dispatch);
  });

  const hydratedMaterialMap = new Map<string, CampaignSaleMaterialItem>();

  materialResponse?.data.results?.map(mapCampaignSaleMaterialItem).forEach((item) => {
    if (item.uuid) {
      hydratedMaterialMap.set(item.uuid, item);
    }
  });

  surveyResponse?.data.items?.map(mapCampaignCustomerSurveyItem).forEach((item) => {
    if (item.uuid) {
      hydratedMaterialMap.set(item.uuid, item);
    }
  });

  return sortedMaterials.map((material) => {
    const hydratedItem = hydratedMaterialMap.get(material.uuid);

    return {
      ...(hydratedItem
        ? {
            uuid: hydratedItem.uuid,
            name: hydratedItem.name,
            section: hydratedItem.section,
            status: hydratedItem.status,
          }
        : buildSelectedSalesMaterialFallbackItem(material)),
      type: material.type,
      order: material.order,
    };
  });
};

export const fetchCampaignSaleMaterialList = async (
  params: CampaignSaleMaterialListParam,
  dispatch?: Dispatch<any>,
): Promise<CampaignSaleMaterialListResponse> => {
  if (isCustomerSurveySection(params.section)) {
    const queryPath = buildCustomerSurveyQueryPath(params);

    return apiClient
      .get<CampaignCustomerSurveyRawResponse>(queryPath)
      .then((response) => ({
        items: (response?.data?.items || []).map(mapCampaignCustomerSurveyItem),
        page: params.page,
        totalPages: response?.data?.totalPages,
        totalRecords: response?.data?.totalRecords,
        hasPrevPage: response?.data?.hasPrevPage,
        hasNextPage: response?.data?.hasNextPage,
      }))
      .catch((err) => {
        throw apiErrorHandler(err, dispatch);
      });
  }

  const queryPath = buildCampaignSaleMaterialQueryPath(params);

  return apiClient
    .get<CampaignSaleMaterialRawResponse>(queryPath, {})
    .then((response) => ({
      items: (response?.data?.results || []).map(mapCampaignSaleMaterialItem),
      page: response?.data?.pagination?.page,
      totalPages: response?.data?.pagination?.pageCount,
      totalRecords: response?.data?.pagination?.total,
      hasPrevPage: response?.data?.pagination?.page > 1,
      hasNextPage: response?.data?.pagination?.page < response?.data?.pagination?.pageCount,
    }))
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchCampaignList = async (
  params: CampaignListParam,
  dispatch?: Dispatch<any>,
): Promise<PaginateList<CampaignItem>> => {
  const queryPath = buildQueryPath(`${ADMIN_URL}/${campaignEndpoint}?`, params);
  return apiClient
    .get<PaginateList<CampaignItem>>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchCampaignFullList = async (
  params: CampaignListParam,
  dispatch?: Dispatch<any>,
): Promise<PaginateList<CampaignItem>> => {
  const queryPath = buildQueryPath(`${ADMIN_URL}/${campaignEndpoint}/list?`, params);
  return apiClient
    .get<PaginateList<CampaignItem>>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchCampaignItem = async (id: string, dispatch?: Dispatch<any>): Promise<CampaignItem> => {
  return apiClient
    .get<CampaignItem>(`${ADMIN_URL}/${campaignEndpoint}/${id}`)
    .then(async (response) => ({
      ...response.data,
      selectedSalesMaterials: await hydrateSelectedSalesMaterials(response.data.selectedSalesMaterials, dispatch),
    }))
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchCampaignDropdownList = async (
  params: CampaignDropdownListParam,
  dispatch?: Dispatch<any>,
): Promise<CampaignDropdownList> => {
  return apiClient
    .get<CampaignDropdownList>(`${ADMIN_URL}/${campaignEndpoint}/dropdown`, { params })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchCampaignLeadList = async (
  id: string,
  params: CampaignLeadListParam,
  sortKeys: { key: string; value?: string }[],
  dispatch?: Dispatch<any>,
): Promise<PaginateList<CampaignLeadItem>> => {
  let queryPath = `${ADMIN_URL}/${campaignEndpoint}/${id}/lead?`;
  forEach(params, (param, key) => {
    if (typeof param === 'object') {
      if (param) {
        if (key.toLowerCase().includes('from')) {
          queryPath += `${key}=${encodeURIComponent(getDayStart(param).toISOString())}&`;
        }
        if (key.toLowerCase().includes('to')) {
          queryPath += `${key}=${encodeURIComponent(getDayEnd(param).toISOString())}&`;
        }
      }
    } else {
      if (key === 'page') {
        queryPath += `${key}=${encodeURIComponent(param as any)}&`;
      } else {
        queryPath += param ? `${key}=${encodeURIComponent(param as any)}&` : '';
      }
    }
  });

  //Query for sorting
  let sortingCount = 0;
  queryPath += `sort=`;
  forEach(sortKeys, (sortParam) => {
    if (sortParam.value) {
      queryPath += `${sortingCount === 0 ? `` : `,`}${encodeURIComponent(sortParam.key)}:${encodeURIComponent(
        sortParam.value,
      )}`;
      sortingCount++;
    }
  });
  if (sortingCount === 0) {
    queryPath += `leadDate:desc`;
  }

  return apiClient
    .get<PaginateList<CampaignLeadItem>>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchLeadItem = async (id: string, dispatch?: Dispatch<any>): Promise<CampaignLeadItem> => {
  return apiClient
    .get<CampaignLeadItem>(`${ADMIN_URL}/${leadEndpoint}/${id}`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchConfig = async (params: ConfigParam, dispatch?: Dispatch<any>): Promise<any> => {
  return apiClient
    .get<any>(`${ADMIN_URL}/${campaignEndpoint}/config`, { params })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const checkDuplicateCampaign = async (id: string, dispatch?: Dispatch<any>): Promise<boolean> => {
  return apiClient
    .get<boolean>(`${ADMIN_URL}/${campaignEndpoint}/duplicate-check/${id}`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const createCampaign = async (body: CreateCampaignBody, dispatch?: Dispatch<any>): Promise<void> => {
  const requestBody = {
    ...body,
    selectedSalesMaterials: normalizeSelectedSalesMaterials(body.selectedSalesMaterials),
  };

  return apiClient
    .post<void>(`${ADMIN_URL}/${campaignEndpoint}`, requestBody)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const modifyCampaign = async (id: string, body: UpdateCampaignBody, dispatch?: Dispatch<any>): Promise<void> => {
  const requestBody = {
    ...body,
    selectedSalesMaterials: normalizeSelectedSalesMaterials(body.selectedSalesMaterials),
  };

  return apiClient
    .put<void>(`${ADMIN_URL}/${campaignEndpoint}/${id}`, requestBody)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateCampaignStatus = async (
  id: string,
  body: UpdateCampaignStatusBody,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  return apiClient
    .put<void>(`${ADMIN_URL}/${campaignEndpoint}/${id}/status`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const deleteCampaign = async (id: string, dispatch?: Dispatch<any>): Promise<void> => {
  return apiClient
    .delete<void>(`${ADMIN_URL}/${campaignEndpoint}/${id}`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const exportCampaignLeadList = async (
  id: string,
  filterParams: CampaignLeadListParam,
  sortKeys: { key: string; value?: string }[],
  dispatch?: Dispatch<any>,
  eachMaxLimit?: boolean,
): Promise<any> => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const lang = getConfig().selectedLang;
  let queryPath = `${ADMIN_URL}/${campaignEndpoint}/${id}/lead/export-csv?timeZone=${timezone}&lang=${lang}&`;
  forEach(filterParams, (param, key) => {
    if (typeof param === 'object') {
      if (param) {
        if (key.toLowerCase().includes('from')) {
          queryPath += `${key}=${encodeURIComponent(getDayStart(param).toISOString())}&`;
        }
        if (key.toLowerCase().includes('to')) {
          queryPath += `${key}=${encodeURIComponent(getDayEnd(param).toISOString())}&`;
        }
      }
    } else {
      if (key === 'page') {
        queryPath += `${key}=${encodeURIComponent(param as any)}&`;
      } else {
        queryPath += param ? `${key}=${encodeURIComponent(param as any)}&` : '';
      }
    }
  });

  //Query for sorting
  let sortingCount = 0;
  queryPath += `sort=`;
  forEach(sortKeys, (sortParam) => {
    if (sortParam.value) {
      queryPath += `${sortingCount === 0 ? `` : `,`}${encodeURIComponent(sortParam.key)}:${encodeURIComponent(
        sortParam.value,
      )}`;
      sortingCount++;
    }
  });
  if (sortingCount === 0) {
    queryPath += `leadDate:desc`;
  }

  const requestConfig: any = {
    responseType: 'blob',
  };

  const apiRequest = eachMaxLimit
    ? apiClient.post(queryPath, {}, requestConfig)
    : apiClient.get(queryPath, requestConfig);

  return apiRequest
    .then((response) => response)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

//PHKL Custom - s
export const getCampaignCategoryList = async (language: string, dispatch?: Dispatch<any>) => {
  const param = {
    lang: language === 'en' ? 'en' : 'zh-HK',
  };

  return apiClient
    .get<CampaignCategoryItem[]>(`${ADMIN_URL}/v2/campaign-category`, { params: param })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};
//PHKL Custom - e
